import { DEFAULT_QUEUE_NAME, EMPTY_NAME } from "@/Settings/Constants/QueueMapping";
import {
	IEmployeeRetrieved,
	IManagedPosition,
	IPositionQueue,
	IQueue,
	IUserQueue,
	IUserWithQueue,
} from "@/Settings/types/QueueMapping/QueueMapping";

const createDefaultQueue = (locationId: string): IQueue => {
	return {
		locationId,
		name: DEFAULT_QUEUE_NAME,
		_id: "",
		usersByPosition: [],
		overflowQueues: [],
	};
};

const validateName = (queueToSearch: IQueue, queues: IQueue[]) => {
	if (!queueToSearch?.name) {
		return false;
	}
	const valueToSearch = queueToSearch.name.trim();
	const isInvalidName = valueToSearch === EMPTY_NAME || valueToSearch === DEFAULT_QUEUE_NAME;
	const isLongName = valueToSearch.length > 100;
	const isAlphanumericName = isAlphanumeric(valueToSearch);

	if (isInvalidName || isLongName || !isAlphanumericName) {
		return false;
	}

	if (queues) {
		const foundQueue = queues.some((queue) => queue.name === valueToSearch && queue._id !== queueToSearch._id);

		if (foundQueue) {
			return false;
		}

		const existOverflowQueue = queues.some((queue) => queue.overflowQueues?.includes(valueToSearch));
		if (existOverflowQueue) {
			return false;
		}
	}

	return true;
};

const validateOverflowName = (overflowQueues: string[], queues: IQueue[]) => {
	return overflowQueues.some((queue) => {
		const valueToSearch = queue.trim();

		if (valueToSearch === EMPTY_NAME || valueToSearch === DEFAULT_QUEUE_NAME) {
			return true;
		}

		const queueNames = queues.map((q) => q.name);

		const existQueueAlready = queueNames.includes(valueToSearch);

		if (existQueueAlready) {
			return true;
		}

		const existingOverFlowQueues = queues.map((q) => q.overflowQueues).flat();

		const existOverflowQueueAlready = existingOverFlowQueues.filter((label) => label === valueToSearch).length > 1;

		if (existOverflowQueueAlready) {
			return true;
		}

		return false;
	});
};

const getLabels = (positions: IManagedPosition[]) => {
	return positions.map((pos) => pos.label) ?? [];
};

const isUserInPosition = (users: IUserQueue[], userId: string) => {
	return users.some((user) => user._id === userId);
};

const getOrderedEmployees = (employees: IUserWithQueue[]) => {
	if (!employees?.length) {
		return [];
	}
	let orderedEmployees = [...employees].sort((a, b) => a.fullName.localeCompare(b.fullName));
	orderedEmployees = orderedEmployees.map((item) => ({ ...item, fullName: item.fullName?.trim() }));
	return orderedEmployees;
};

const getTransformedUsersQueue = (users: IUserWithQueue[]): IUserQueue[] => {
	return users.map((user) => {
		const userQueue: IUserQueue = {
			_id: user._id,
			name: user.fullName,
		};
		return userQueue;
	});
};

const getTransformedUsers = (employees: IEmployeeRetrieved[]): IUserWithQueue[] => {
	if (!employees?.length) {
		return [];
	}

	return employees
		.filter((employee) => employee.payrollSourceId?.idField === "workerId")
		.map((employee) => ({
			_id: employee.payrollSourceId.idValue,
			fullName: employee.fullName,
			employeeId: employee.payrollSourceId.idValue,
			position: "Other Position",
			hasQueue: null,
		}));
};

const getUniqueUsers = (queues: IQueue[] | null | undefined) => {
	const users: string[] = [];
	if (!queues?.length) {
		return users;
	}

	queues.forEach((queue) => {
		if (!queue.usersByPosition?.length) {
			return;
		}

		queue.usersByPosition.forEach((pos) => {
			if (!pos.users?.length) {
				return;
			}
			const posUsers = pos.users.map((user) => user.name as string);
			users.push(...posUsers);
		});
	});

	return users;
};

const getUpdatedUsersAtHandleSelect = (selectedUsers: IPositionQueue[], position: string, user: IUserQueue) => {
	return selectedUsers
		.map((pos) => {
			if (pos.name !== position) {
				return pos;
			}
			const isUserAlreadySelected = pos.users.some((u) => u._id === user._id);
			if (isUserAlreadySelected) {
				return { ...pos, users: pos.users.filter((u) => u._id !== user._id) };
			}
			return { ...pos, users: [...pos.users, user] };
		})
		.filter((pos) => pos.users.length > 0);
};

const buildQueueLabelsObjectList = (queueLabels: string[]) => {
	return queueLabels?.map((label) => ({ id: label.replaceAll(" ", ""), label })) ?? [];
};

const isAlphanumeric = (str: string): boolean => {
	if (!str) {
		return false;
	}
	const alphanumericRegex = /\S+/;
	return alphanumericRegex.test(str);
};

export {
	createDefaultQueue,
	validateName,
	getLabels,
	isUserInPosition,
	getOrderedEmployees,
	getTransformedUsersQueue,
	getTransformedUsers,
	getUniqueUsers,
	validateOverflowName,
	getUpdatedUsersAtHandleSelect,
	buildQueueLabelsObjectList,
	isAlphanumeric,
};
